import { Button, Typography } from '@recruit-robin/robin-theme/web-components';
import { ArrowLeft, Pencil_01, Upload_01 } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useEffect, useRef, useState } from 'react';

import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useContextSelector } from 'use-context-selector';
import { useCreateEnrichedVacancy } from '../../hooks/useCreateEnrichedVacancy';
import { EnrichVacancyModalContext } from '../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalState } from '../EnrichVacancyModalContext/EnrichVacancyModalContext.types';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import { CreateFromFileUpload } from './components/CreateFromFileUpload/CreateFromFileUpload';
import { CreateFromJobDescription } from './components/CreateFromJobDescription/CreateFromJobDescription';
import { CreateFromTemplate } from './components/CreateFromTemplate/CreateFromTemplate';
import { useStyles } from './CreateEnrichedVacancy.styles';

export const CreateEnrichedVacancy = () => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const [method, setMethod] = useState<'text' | 'template' | 'file' | null>(null);
  const state = useContextSelector(EnrichVacancyModalContext, (s) => s.state);
  const vacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.vacancy);
  const didRunCreateEnrichedVacancyRef = useRef(false);
  const { handleCreateFromTemplate } = useCreateEnrichedVacancy();

  useEffect(() => {
    if (
      vacancy &&
      state === EnrichVacancyModalState.WaitingToEnrichVacancy &&
      !didRunCreateEnrichedVacancyRef.current
    ) {
      didRunCreateEnrichedVacancyRef.current = true;

      handleCreateFromTemplate(vacancy);
    }
  }, [vacancy, state]);

  if (state === EnrichVacancyModalState.EnrichingVacancy) {
    return <LoadingPage message={dictionary.reviewingYourVacancyForQuality} />;
  }

  const handleGoBackToHere = () => {
    setMethod(null);
  };

  const goBackButtonComponent = (
    <Button
      style="text"
      type="subtle2"
      startIcon={<ArrowLeft size={18} />}
      label={dictionary.back}
      onClick={handleGoBackToHere}
    />
  );

  return (
    <div css={classes.container}>
      {method === null && (
        <div css={classes.selectingMethod}>
          <div css={classes.header}>
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[70]}>
              {dictionary.createAJob}
            </Typography>
            <Typography variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
              {dictionary.enrichVacancyChooseMethod}
            </Typography>
          </div>

          <div css={classes.actions}>
            <Button
              style="filled"
              type="default"
              startIcon={<Pencil_01 size={18} />}
              label={dictionary.provideText}
              onClick={() => setMethod('text')}
            />

            <CreateFromTemplate />

            <Button
              style="filled"
              type="default"
              startIcon={<Upload_01 size={18} />}
              label={dictionary.uploadFile}
              onClick={() => setMethod('file')}
            />
          </div>
        </div>
      )}

      {method === 'text' && <CreateFromJobDescription goBackButton={goBackButtonComponent} />}
      {method === 'file' && <CreateFromFileUpload goBackButton={goBackButtonComponent} />}
    </div>
  );
};
